import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLanguage, Language } from "@/contexts/LanguageContext";
import logo from "@/assets/LOGO-HORIZONTAL-BRANCA-DOURADO.png";
import PracticeAreasDropdown from "@/components/PracticeAreasDropdown";
import LocationsDropdown from "@/components/LocationsDropdown";
import LocalLink from "@/components/LocalLink";
import ResourcesDropdown from "@/components/ResourcesDropdown";
import { setMobileMenuOpen } from "@/hooks/useMobileMenuOpen";

const languageConfig: { code: Language; flag: string; label: string }[] = [
  { code: "EN", flag: "🇺🇸", label: "English" },
  { code: "PT", flag: "🇧🇷", label: "Português" },
  { code: "ES", flag: "🇪🇸", label: "Español" },
];

const LOCALE_RE = /^\/(en|pt|es)(\/.*)?$/i;
const buildLocalizedPath = (pathname: string, lang: Language, search = "", hash = "") => {
  const slug = lang.toLowerCase();
  const match = pathname.match(LOCALE_RE);
  const rest = match ? match[2] || "/" : pathname || "/";
  const normalized = rest === "/" ? "" : rest;
  return `/${slug}${normalized}${search}${hash}`;
};

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const { language, setLanguage, t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === "/" || /^\/(en|pt|es)\/?$/i.test(location.pathname);

  const switchLanguage = (lang: Language) => {
    setLanguage(lang);
    const target = buildLocalizedPath(location.pathname, lang, location.search, location.hash);
    navigate(target);
  };

  const navLinks = [
    { name: t("nav.home"), href: "#home", isAnchor: true },
    { name: t("nav.about"), href: "#about", isAnchor: true },
    { name: t("nav.contact"), href: "/contact", isAnchor: false },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Lock background scroll + hide the sticky action bar while the mobile menu is open
  useEffect(() => {
    setMobileMenuOpen(isMobileMenuOpen);
    if (isMobileMenuOpen) {
      const previous = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = previous;
      };
    }
  }, [isMobileMenuOpen]);

  // Close the mobile menu whenever the route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const headerHeight = isScrolled ? 64 : 88;


  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? "bg-navy/80 backdrop-blur-md shadow-lg py-2"
          : "bg-navy/30 backdrop-blur-sm py-4"
      }`}
      style={{
        borderBottom: isScrolled
          ? "1px solid transparent"
          : "none",
        borderImage: isScrolled
          ? "linear-gradient(to right, hsl(213 70% 11%), hsl(45 100% 51%), transparent) 1"
          : "none",
      }}
    >
      <div className="container-custom">
        <nav className="flex items-center justify-between">
          {/* Logo - Horizontal Version for Navbar */}
          <LocalLink to="/" className="flex items-center cursor-pointer">
            <img 
              src={logo} 
              alt="Arandia Law Firm" 
              className="h-8 md:h-10 w-auto object-contain"
            />
          </LocalLink>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.slice(0, 2).map((link) => (
              isHomePage ? (
                <a
                  key={link.href}
                  href={link.href}
                  className="text-sm font-medium text-white/80 hover:text-gold transition-colors"
                >
                  {link.name}
                </a>
              ) : (
                <LocalLink
                  key={link.href}
                  to={`/${link.href}`}
                  className="text-sm font-medium text-white/80 hover:text-gold transition-colors"
                >
                  {link.name}
                </LocalLink>
              )
            ))}
            <PracticeAreasDropdown />
            <LocationsDropdown />
            <ResourcesDropdown />

            {navLinks.slice(2).map((link) => (
              <LocalLink
                key={link.href}
                to={link.href}
                className="text-sm font-medium text-white/80 hover:text-gold transition-colors"
              >
                {link.name}
              </LocalLink>
            ))}
          </div>

          {/* Right Side Actions */}
          <div className="hidden lg:flex items-center gap-4">
            {/* Language Selector */}
            <div className="relative">
              <button
                onClick={() => setIsLangOpen(!isLangOpen)}
                className="flex items-center gap-2 text-sm font-medium text-white/80 hover:text-gold transition-colors"
              >
                <span className="text-base">{languageConfig.find(l => l.code === language)?.flag}</span>
                {language}
              </button>
              {isLangOpen && (
                <div
                  className="absolute top-full right-0 mt-2 bg-navy-light rounded-md shadow-lg overflow-hidden border border-white/10 animate-fade-in"
                >
                  {languageConfig.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        switchLanguage(lang.code);
                        setIsLangOpen(false);
                      }}
                      className={`flex items-center gap-2 w-full px-4 py-2 text-sm text-left transition-colors ${
                        language === lang.code 
                          ? "bg-gold text-navy" 
                          : "text-white hover:bg-white/10"
                      }`}
                    >
                      <span className="text-base">{lang.flag}</span>
                      {lang.code}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* CTA Button */}
            {isHomePage ? (
              <a href="#contact" className="btn-gold text-sm whitespace-nowrap">
                {t("nav.cta")}
              </a>
            ) : (
              <LocalLink to="/contact" className="btn-gold text-sm whitespace-nowrap">
                {t("nav.cta")}
              </LocalLink>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-white"
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-menu"
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </nav>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div
            id="mobile-menu"
            className="lg:hidden fixed left-3 right-3 rounded-2xl bg-navy/95 backdrop-blur-xl shadow-[0_24px_60px_rgba(0,0,0,0.5)] border border-white/10 overflow-hidden motion-safe:animate-fade-in"
            style={{ top: headerHeight + 8, maxHeight: "calc(100vh - 6rem)" }}
          >
            {/* Gold hairline along the top edge */}
            <div className="h-px bg-gradient-to-r from-transparent via-gold to-transparent" />

            <div className="overflow-y-auto p-4" style={{ maxHeight: "calc(100vh - 6rem - 1px)" }}>
              <div className="space-y-0.5">
                {navLinks.slice(0, 2).map((link) => (
                  isHomePage ? (
                    <a
                      key={link.href}
                      href={link.href}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="flex items-center min-h-[48px] pl-2 text-base font-medium text-white hover:text-gold transition-colors border-b border-white/5"
                    >
                      {link.name}
                    </a>
                  ) : (
                    <LocalLink
                      key={link.href}
                      to={`/${link.href}`}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="flex items-center min-h-[48px] pl-2 text-base font-medium text-white hover:text-gold transition-colors border-b border-white/5"
                    >
                      {link.name}
                    </LocalLink>
                  )
                ))}
                <div className="border-b border-white/5 ml-2">
                  <PracticeAreasDropdown mobile onNavigate={() => setIsMobileMenuOpen(false)} />
                </div>
                <div className="border-b border-white/5 ml-2">
                  <LocationsDropdown mobile onNavigate={() => setIsMobileMenuOpen(false)} />
                </div>
                <div className="border-b border-white/5 ml-2">
                  <ResourcesDropdown mobile onNavigate={() => setIsMobileMenuOpen(false)} />
                </div>
                {navLinks.slice(2).map((link) => (
                  <LocalLink
                    key={link.href}
                    to={link.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center min-h-[48px] pl-2 text-base font-medium text-white hover:text-gold transition-colors"
                  >
                    {link.name}
                  </LocalLink>
                ))}
              </div>

              {/* Footer area */}
              <div className="mt-4 pt-5 border-t border-white/10 space-y-3">
                <div className="flex gap-2">
                  {languageConfig.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => switchLanguage(lang.code)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md transition-colors ${
                        language === lang.code
                          ? "bg-gold text-navy"
                          : "bg-white/10 text-white hover:bg-white/20"
                      }`}
                    >
                      <span>{lang.flag}</span>
                      {lang.code}
                    </button>
                  ))}
                </div>
                {isHomePage ? (
                  <a
                    href="#contact"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="btn-gold block w-full text-center text-sm"
                  >
                    {t("nav.cta")}
                  </a>
                ) : (
                  <LocalLink
                    to="/contact"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="btn-gold block w-full text-center text-sm"
                  >
                    {t("nav.cta")}
                  </LocalLink>
                )}
              </div>
            </div>
          </div>
        )}


      </div>
    </header>
  );
};

export default Header;
